import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import AppTheme from '../AppTheme';
import ColorModeSelect from '../customizations/ColorModeSelect';
import Sitemark from '../components/SitemarkIcon';
import config from '../config';
const API_BASE_URL = `${config.server}/api`;
const PROD_HOSTS = ['economicmessage.com', 'www.economicmessage.com', 'charity.dpdns.org'];
const cloudflare_key = PROD_HOSTS.includes(window.location.hostname)
  ? '0x4AAAAAAB1oTW9OdFTzJZyh'
  : '1x00000000000000000000AA';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignUpContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

export default function SignUp(props) {
  const [usernameError, setUsernameError] = React.useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [agreedToClaims, setAgreedToClaims] = React.useState(false);
  const [cfToken, setCfToken] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState('error');
  const [turnstileLoaded, setTurnstileLoaded] = React.useState(false);
  const [turnstileError, setTurnstileError] = React.useState(false);
  
  const turnstileRef = React.useRef(null);
  const turnstileWidgetId = React.useRef(null);

  const validateInputs = () => {
    const username = document.getElementById('username').value;
    const password = document.getElementById('password').value;
    let isValid = true;
    
    if (!username || !/^[a-z]{3,20}$/.test(username)) {
      setUsernameError(true);
      setUsernameErrorMessage('用戶名必須為3-20位英文小寫字母');
      isValid = false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage('');
    }
    
    if (!password || !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&,])[A-Za-z\d@$!%*?&,]{13,30}$/.test(password)) {
      setPasswordError(true);
      setPasswordErrorMessage('密碼須由字母數字特殊字符@$!%*?&,構成且不小於13位');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }
    
    return isValid;
  };

  const handleTurnstileSuccess = (token) => {
    console.log('Turnstile token received:', token);
    setCfToken(token);
    setTurnstileError(false);
    setAlertMessage('');
  };

  const handleTurnstileError = (error) => {
    console.error('Turnstile error:', error);
    setTurnstileError(true);
    setCfToken('');
    setAlertMessage('真人驗證失敗，請重新整理頁面重試');
    setAlertSeverity('error');
  };

  const handleTurnstileExpired = () => {
    console.log('Turnstile token expired');
    setCfToken('');
    setTurnstileError(true);
    setAlertMessage('驗證已過期，請重新驗證');
    setAlertSeverity('warning');
  };

  const submitForm = async () => {
    const username = document.getElementById('username').value;
    const password = document.getElementById('password').value;

    try {
      // 修改请求 URL 以匹配后端
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          cf_response: cfToken, // 修正参数名称
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', [...response.headers.entries()]);

      // 检查响应是否为 JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Server returned non-JSON response:', text);
        throw new Error('伺服器回應格式錯誤');
      }

      const result = await response.json();
      console.log('Response result:', result);

      if (response.ok) {
        // 注意：不要在生产环境中使用 localStorage 存储敏感信息
        if (result.access_token) {
          localStorage.setItem('access_token', result.access_token);
        }
        setAlertMessage('註冊成功！正在跳轉...');
        setAlertSeverity('success');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        // 处理不同类型的错误响应
        let errorMessage = '註冊失敗';
        
        if (result.detail) {
          // 检查 detail 是否为数组
          if (Array.isArray(result.detail)) {
            errorMessage = result.detail.map(err => {
              if (typeof err === 'object' && err.msg) {
                return err.msg;
              }
              return String(err);
            }).join(', ');
          } else if (typeof result.detail === 'object' && result.detail.msg) {
            errorMessage = result.detail.msg;
          } else {
            errorMessage = String(result.detail);
          }
        } else if (result.message) {
          errorMessage = String(result.message);
        }
        
        setAlertMessage(errorMessage);
        setAlertSeverity('error');
        
        // 如果是 Turnstile 验证失败，重置 token
        if (errorMessage.includes('turnstile') || errorMessage.includes('驗證')) {
          setCfToken('');
          resetTurnstile();
        }
      }
    } catch (error) {
      console.error('註冊錯誤:', error);
      setAlertMessage(error.message || '網絡錯誤，請稍後重試');
      setAlertSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetId.current) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.error('Reset turnstile error:', error);
      }
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (loading) return; // 防止重複提交
    
    if (!validateInputs()) {
      return;
    }

    if (!agreedToClaims) {
      setAlertMessage('請先閱讀並同意網站聲明');
      setAlertSeverity('warning');
      return;
    }

    if (!turnstileLoaded) {
      setAlertMessage('正在載入驗證組件，請稍候...');
      setAlertSeverity('info');
      return;
    }

    if (!cfToken) {
      setAlertMessage('請完成真人驗證');
      setAlertSeverity('warning');
      return;
    }

    setLoading(true);
    submitForm();
  };

  // 初始化 Turnstile
  const initTurnstile = React.useCallback(() => {
    if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current) {
      return;
    }

    try {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: cloudflare_key,
        callback: handleTurnstileSuccess,
        'error-callback': handleTurnstileError,
        'expired-callback': handleTurnstileExpired,
        theme: 'auto',
        size: 'normal',
      });
      
      setTurnstileLoaded(true);
      console.log('Turnstile initialized with widget ID:', turnstileWidgetId.current);
    } catch (error) {
      console.error('Failed to initialize Turnstile:', error);
      setTurnstileError(true);
      setAlertMessage('驗證組件載入失敗，請重新整理頁面');
      setAlertSeverity('error');
    }
  }, []);

  // 載入 Cloudflare Turnstile 腳本
  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('Turnstile script loaded');
      // 等待一小段時間確保 window.turnstile 可用
      setTimeout(initTurnstile, 100);
    };
    
    script.onerror = (error) => {
      console.error('Failed to load Turnstile script:', error);
      setTurnstileError(true);
      setAlertMessage('驗證組件載入失敗，請檢查網絡連接');
      setAlertSeverity('error');
    };

    document.head.appendChild(script);
    
    return () => {
      try {
        document.head.removeChild(script);
      } catch (error) {
        console.warn('Failed to remove script:', error);
      }
    };
  }, [initTurnstile]);

  // 當 Turnstile 腳本載入後初始化
  React.useEffect(() => {
    if (window.turnstile && !turnstileWidgetId.current) {
      initTurnstile();
    }
  }, [initTurnstile]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
      <SignUpContainer direction="column" justifyContent="space-between">
        <Card variant="outlined">
          <Sitemark />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            註冊
          </Typography>
          
          {alertMessage && (
            <Alert severity={alertSeverity} sx={{ mb: 2 }}>
              {alertMessage}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <FormControl>
              <FormLabel htmlFor="username">用戶名</FormLabel>
              <TextField
                required
                fullWidth
                id="username"
                placeholder="3-20位英文小寫字母"
                name="username"
                autoComplete="username"
                variant="outlined"
                error={usernameError}
                helperText={usernameErrorMessage}
                color={usernameError ? 'error' : 'primary'}
                disabled={loading}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password">密碼</FormLabel>
              <TextField
                required
                fullWidth
                name="password"
                placeholder="密碼須由字母數字特殊字符@$!%*?&,構成且不小於13位"
                type="password"
                id="password"
                autoComplete="new-password"
                variant="outlined"
                error={passwordError}
                helperText={passwordErrorMessage}
                color={passwordError ? 'error' : 'primary'}
                disabled={loading}
              />
            </FormControl>

            {/* Turnstile 容器 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <div ref={turnstileRef}></div>
              {!turnstileLoaded && !turnstileError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    載入驗證組件中...
                  </Typography>
                </Box>
              )}
              {turnstileError && (
                <Typography variant="body2" color="error">
                  驗證組件載入失敗，請重新整理頁面
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToClaims}
                  onChange={(e) => setAgreedToClaims(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label={
                <Typography>
                  我已閱讀
                  <Link 
                    href="/declaration" 
                    target="_blank"
                    sx={{ mx: 0.5, textDecoration: 'underline' }}
                  >
                    網站聲明
                  </Link>
                </Typography>
              }
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !turnstileLoaded || !cfToken || turnstileError}
              sx={{ position: 'relative' }}
            >
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-10px',
                  }}
                />
              )}
              {loading ? '註冊中...' : '註冊'}
            </Button>
          </Box>

          <Divider>
            <Typography sx={{ color: 'text.secondary' }}>或</Typography>
          </Divider>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ textAlign: 'center' }}>
              已經有帳戶？{' '}
              <Link
                href="/signin"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                直接登入
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignUpContainer>
    </AppTheme>
  );
}